import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSesja } from '@/hooks/useSesja';
import {
  czyBrakTabeli,
  odczytajLokalne,
  przelacz,
  scal,
  wyczyscLokalne,
  zapiszLokalne,
} from '@/lib/favorites';

/**
 * Ulubione oferty (SRCH-13) — jedno źródło stanu serduszka dla karty w wynikach, dymka na
 * mapie i strony oferty.
 *
 * Sesję bierze z `useSesja` — jednego magazynu modułowego na stronę, nie jednej subskrypcji
 * na serduszko (lista renderuje kilkadziesiąt kart naraz).
 *
 * DZIAŁA PRZED MIGRACJĄ. Dopóki `flat.favorites` nie istnieje, zalogowany dostaje dokładnie
 * to samo, co niezalogowany: listę w przeglądarce. Rozpoznajemy to raz, po pierwszym
 * nieudanym odczycie, i zapamiętujemy na czas życia karty — żeby nie łomotać do bazy przy
 * każdym kliknięciu serduszka.
 *
 * Konwencja 29: cała ta warstwa jest wygodą, nie zabezpieczeniem. Cudzych ulubionych pilnuje
 * RLS (`user_id = auth.uid()`), nie ten plik.
 */

// ─── tryb awaryjny: tabeli jeszcze nie ma ─────────────────────────────────────────────
// Modułowa, nie stanowa, i to jest świadome: gdyby to był `useState`, każda z kilkudziesięciu
// kart odkrywałaby brak tabeli osobno, a każde odkrycie przerysowywałoby listę.
let brakTabeli = false;

export type TrybUlubionych = 'przegladarka' | 'konto';

const KLUCZ = (authId: string | null) => ['flat.ulubione', authId ?? 'gosc'] as const;

async function wczytajZKonta(): Promise<string[] | 'brak-tabeli'> {
  const { data, error } = await supabase
    .schema('flat' as never)
    .from('favorites' as never)
    .select('offer_id, created_at')
    .order('created_at', { ascending: false });
  if (error) {
    if (czyBrakTabeli(error)) return 'brak-tabeli';
    throw error;
  }
  return ((data ?? []) as unknown as { offer_id: string }[]).map((r) => r.offer_id);
}

/**
 * Przeniesienie listy z przeglądarki na konto. Robione RAZ, przy pierwszym udanym odczycie
 * po zalogowaniu: to jest dokładnie ten moment, w którym użytkownik zrobił to, o co go
 * prosiliśmy, więc nie może stracić tego, co uzbierał wcześniej.
 *
 * Kasujemy listę lokalną dopiero PO udanym zapisie — odwrotna kolejność gubi ulubione przy
 * zerwanym połączeniu.
 */
/**
 * Dopisanie par (user, oferta) BEZ duplikatów i bez kasowania czegokolwiek na koncie:
 * `ON CONFLICT DO NOTHING` na kluczu głównym (user_id, offer_id) — wystarczy grant INSERT.
 * Idempotentne: druga karta albo drugie wejście dopisują zero wierszy, a nie łapią 23505.
 */
function dopisz(authId: string, ids: string[]) {
  return supabase
    .schema('flat' as never)
    .from('favorites' as never)
    .upsert(ids.map((offer_id) => ({ user_id: authId, offer_id })) as never, {
      onConflict: 'user_id,offer_id',
      ignoreDuplicates: true,
    } as never);
}

/** 23503: oferta z listy lokalnej nie istnieje już w `flat.offers` (klucz obcy). */
const BRAK_OFERTY = '23503';

async function przeniesNaKonto(authId: string, zKonta: string[]): Promise<string[]> {
  const lokalne = odczytajLokalne();
  if (lokalne.length === 0) return zKonta;
  const brakujace = lokalne.filter((id) => !zKonta.includes(id));
  let przeniesione = brakujace;
  if (brakujace.length > 0) {
    const { error } = await dopisz(authId, brakujace);
    if (error && czyBrakTabeli(error)) return scal(zKonta, lokalne); // tabeli nie ma — lista lokalna ZOSTAJE
    if (error && error.code === BRAK_OFERTY) {
      // Jedna nieistniejąca oferta wywraca cały wsad — dopisujemy więc po jednej i pomijamy
      // tylko te, których w bazie już nie ma (konto i tak by ich nie trzymało: klucz obcy).
      przeniesione = [];
      for (const id of brakujace) {
        const { error: e } = await dopisz(authId, [id]);
        if (!e) przeniesione.push(id);
        else if (e.code !== BRAK_OFERTY) throw e;
      }
    } else if (error) {
      throw error;
    }
  }
  // Lokalną czyścimy dopiero PO udanym zapisie — odwrotna kolejność gubi ulubione przy zerwanym
  // połączeniu.
  wyczyscLokalne();
  return scal(zKonta, przeniesione);
}

export interface Ulubione {
  ids: string[];
  czyUlubiona: (offerId: string) => boolean;
  przelaczUlubiona: (offerId: string) => void;
  /** `przegladarka` = lista siedzi tylko na tym urządzeniu. */
  tryb: TrybUlubionych;
  /**
   * Czy w ogóle jest sesja. OSOBNO od `tryb`, i to nie jest nadmiarowe: przed migracją
   * zalogowany też siedzi w trybie przeglądarki, a zachęta „załóż konto" pokazana komuś,
   * kto konto MA, jest po prostu nieprawdziwa.
   */
  zalogowany: boolean;
  gotowe: boolean;
}

export function useFavorites(): Ulubione {
  const sesjaTeraz = useSesja();
  const authId = sesjaTeraz?.user?.id ?? null;
  const qc = useQueryClient();

  const q = useQuery<string[]>({
    queryKey: KLUCZ(authId),
    // Dopóki nie wiemy, czy ktoś jest zalogowany, nie zgadujemy — serduszko czeka.
    enabled: sesjaTeraz !== undefined,
    staleTime: 30_000,
    queryFn: async () => {
      if (!authId || brakTabeli) return odczytajLokalne();
      const zKonta = await wczytajZKonta();
      if (zKonta === 'brak-tabeli') {
        brakTabeli = true;
        return odczytajLokalne();
      }
      return przeniesNaKonto(authId, zKonta);
    },
  });

  // `?? []` tworzy nową tablicę przy każdym renderze, a ta trafia do zależności dwóch
  // useCallback poniżej — bez useMemo obie funkcje byłyby nowe za każdym razem i każde
  // serduszko na liście przerysowywałoby się bez powodu.
  const ids = useMemo(() => q.data ?? [], [q.data]);
  const naKoncie = !!authId && !brakTabeli;

  // `byla` jest wyliczane PRZY KLIKNIĘCIU i podawane do mutacji, a nie odczytywane w
  // `mutationFn` z pamięci podręcznej. To nie jest kosmetyka: `onMutate` zdąży już wtedy
  // wpisać stan optymistyczny, więc odczyt z pamięci pokazuje stan PO przełączeniu i każde
  // polubienie szłoby do bazy jako `delete`. Znalazł to test, nie przegląd kodu.
  const mutacja = useMutation({
    mutationFn: async ({ offerId, byla }: { offerId: string; byla: boolean }) => {
      if (!naKoncie) {
        zapiszLokalne(przelacz(odczytajLokalne(), offerId));
        return;
      }
      const zapytanie = supabase.schema('flat' as never).from('favorites' as never);
      // Dodanie przez `dopisz` (ON CONFLICT DO NOTHING): podwójne dotknięcie albo druga karta nie
      // kończą się 23505.
      const { error } = byla
        ? await zapytanie.delete().eq('offer_id', offerId)
        : await dopisz(authId as string, [offerId]);
      if (error) {
        if (!czyBrakTabeli(error)) throw error;
        // Tabela zniknęła nam pod ręką (albo jej nigdy nie było) — przechodzimy na
        // przeglądarkę i zapisujemy tam, zamiast gubić kliknięcie.
        brakTabeli = true;
        zapiszLokalne(przelacz(odczytajLokalne(), offerId));
      }
    },
    // Serduszko ma reagować natychmiast; sieć dogoni.
    onMutate: async ({ offerId }: { offerId: string; byla: boolean }) => {
      await qc.cancelQueries({ queryKey: KLUCZ(authId) });
      const poprzednie = qc.getQueryData<string[]>(KLUCZ(authId)) ?? [];
      qc.setQueryData<string[]>(KLUCZ(authId), przelacz(poprzednie, offerId));
      return { poprzednie };
    },
    onError: (_b, _v, kontekst) => {
      if (kontekst) qc.setQueryData<string[]>(KLUCZ(authId), kontekst.poprzednie);
    },
    onSettled: () => { qc.invalidateQueries({ queryKey: KLUCZ(authId) }); },
  });

  const przelaczUlubiona = useCallback(
    (offerId: string) => { mutacja.mutate({ offerId, byla: ids.includes(offerId) }); },
    [mutacja, ids],
  );
  const czyUlubiona = useCallback((offerId: string) => ids.includes(offerId), [ids]);

  return {
    ids,
    czyUlubiona,
    przelaczUlubiona,
    tryb: naKoncie ? 'konto' : 'przegladarka',
    zalogowany: !!authId,
    gotowe: sesjaTeraz !== undefined && !q.isPending,
  };
}
